import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { WindowRefService } from '../../../../services/window-ref.service';
import { HotelToursSearchParams } from '../../../../types/interfaces/hotel-tour-search';

@Component({
  selector: 'app-hotel-stay-calendar',
  templateUrl: './stay-calendar.component.html',
  standalone: true,
  imports: [DatePipe],
})
export class HotelStayCalendarComponent implements OnInit {
  @ViewChild("monthsScroller") monthsScroller: ElementRef<HTMLElement>;
  @Input() initialSearchParams: HotelToursSearchParams;
  dateFrom: string;
  dateTo: string;
  activeField: 'checkIn' | 'checkOut' = 'checkIn';
  months: { date: Date; cells: { key: string; day: number }[] }[] = [];
  readonly today = this.dateKey(new Date());
  readonly labels = this.windowRef.nativeWindow.app.translations.hotelStayCalendar;

  constructor(private windowRef: WindowRefService) {}

  ngOnInit() {
    this.dateFrom = this.initialSearchParams.dateFrom.slice(0, 10);
    this.dateTo = this.initialSearchParams.dateTo.slice(0, 10);
    const start = this.parseDate(this.today);
    // Include existing future bookings as well as a year from the selected month.
    this.appendMonths(start, 12);
    this.ensureCheckoutMonth();
  }

  selectField(field: 'checkIn' | 'checkOut') {
    this.activeField = field;
    this.scrollToActiveDate();
  }

  scrollToActiveDate() {
    const scroller = this.monthsScroller.nativeElement;
    const date = this.activeField === 'checkIn' ? this.dateFrom : this.dateTo;
    const month = scroller.querySelector<HTMLElement>(`[data-month="${date.slice(0, 7)}"]`);
    if (month) scroller.scrollTop = month.offsetTop;
  }

  canSelect(key: string): boolean {
    return !!key && key >= this.today && (this.activeField === 'checkIn' || key > this.dateFrom);
  }

  selectDate(key: string) {
    if (!this.canSelect(key)) return;
    if (this.activeField === 'checkIn') {
      this.dateFrom = key;
      if (this.dateTo <= key) {
        const next = this.parseDate(key);
        next.setDate(next.getDate() + 1);
        this.dateTo = this.dateKey(next);
      }
      this.ensureCheckoutMonth();
      this.activeField = 'checkOut';
    } else {
      this.dateTo = key;
    }
  }

  get canApply(): boolean {
    return this.dateFrom >= this.today && this.dateTo > this.dateFrom;
  }

  get selection() {
    return { dateFrom: this.dateFrom, dateTo: this.dateTo, forceDates: 1 };
  }

  loadMore() {
    const last = this.months[this.months.length - 1].date;
    this.appendMonths(new Date(last.getFullYear(), last.getMonth() + 1, 1), 6);
  }

  private ensureCheckoutMonth() {
    const checkOut = this.parseDate(this.dateTo);
    const monthStart = new Date(checkOut.getFullYear(), checkOut.getMonth(), 1);
    while (this.months[this.months.length - 1].date < monthStart) {
      this.loadMore();
    }
  }

  private appendMonths(start: Date, count: number) {
    for (let index = 0; index < count; index++) {
      const date = new Date(start.getFullYear(), start.getMonth() + index, 1);
      const cells = Array.from({ length: (date.getDay() + 6) % 7 }, () => ({ key: '', day: 0 }));
      const total = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      for (let day = 1; day <= total; day++) {
        cells.push({ key: this.dateKey(new Date(date.getFullYear(), date.getMonth(), day)), day });
      }
      this.months.push({ date, cells });
    }
  }

  private parseDate(key: string) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private dateKey(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
